import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'

export default function useRiskMap() {
  const { handleError } = useCommon()
  const { getItemsAggregate } = useLambda()
  const { client } = store.state?.userStore?.userData

  const companyFilter = computed(() => store.state.filtersStore.companyFilter)
  const idFilter = computed(() => store.state.filtersStore.idFilter)

  const riskType = ref('inherent')
  const isLoading = ref(true)
  const matrixData = ref({})
  const treatmentsInCell = ref([])
  const selectedCell = ref(null)

  const LEVELS = [
    { key: 'Crítico', min: 20, max: 25, color: '#823535' },
    { key: 'Muy Alto', min: 15, max: 19, color: '#C00000' },
    { key: 'Alto', min: 10, max: 14, color: '#ED7D31' },
    { key: 'Medio', min: 5, max: 9, color: '#FFC000' },
    { key: 'Bajo', min: 1, max: 4, color: '#70AD47' },
  ]

  function getRiskLevel(value) {
    for (const level of LEVELS) {
      if (value >= level.min && value <= level.max) return level
    }
    return LEVELS[4]
  }

  function getCellColor(value) {
    return getRiskLevel(value).color
  }

  const summaryTable = computed(() => {
    const counts = { 'Crítico': 0, 'Muy Alto': 0, 'Alto': 0, 'Medio': 0, 'Bajo': 0 }
    let total = 0

    for (const key in matrixData.value) {
      const items = matrixData.value[key]
      if (!items || !items.length) continue
      const [p, i] = key.split('-').map(Number)
      const risk = p * i
      const level = getRiskLevel(risk)
      counts[level.key] += items.length
      total += items.length
    }

    return LEVELS.map(level => ({
      level: level.key,
      color: level.color,
      count: counts[level.key],
      percentage: total ? Math.round((counts[level.key] / total) * 100) : 0,
      avgRisk: calculateAvgRisk(level.key),
    }))
  })

  function calculateAvgRisk(levelKey) {
    let sum = 0
    let count = 0

    for (const key in matrixData.value) {
      const items = matrixData.value[key]
      if (!items || !items.length) continue
      const [p, i] = key.split('-').map(Number)
      const risk = p * i
      const level = getRiskLevel(risk)
      if (level.key === levelKey) {
        sum += risk * items.length
        count += items.length
      }
    }

    return count ? Math.round((sum / count) * 10) / 10 : 0
  }

  const totalTreatments = computed(() => {
    let total = 0
    for (const key in matrixData.value) {
      if (matrixData.value[key]) total += matrixData.value[key].length
    }
    return total
  })

  const fetchRiskData = async () => {
    isLoading.value = true
    matrixData.value = {}

    try {
      const collection = riskType.value === 'inherent' ? 'inherentAnalysis' : 'residualAnalysis'
      const pField = riskType.value === 'inherent' ? 'calculatedP' : 'residualP'
      const iField = riskType.value === 'inherent' ? 'calculatedI' : 'residualI'
      const levelField = riskType.value === 'inherent' ? 'inherentLevel' : 'residualLevel'

      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true },
        [pField]: { $exists: true, $ne: null },
        [iField]: { $exists: true, $ne: null },
      }

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'treatment',
            localField: 'treatment_id',
            foreignField: '_id',
            as: 'treatment',
            pipeline: [
              {
                $project: {
                  treatmentId: 1,
                  nombre: 1,
                  empresa_id: 1,
                  departamento_id: 1,
                }
              }
            ]
          }
        },
        {
          $addFields: {
            treatment: { $arrayElemAt: ['$treatment', 0] },
          }
        },
      ]

      const conditions = []

      if (companyFilter.value.length > 0) {
        conditions.push({ 'treatment.empresa_id': { $in: companyFilter.value.map(e => oid(e)) } })
      }

      if (idFilter.value.length > 0) {
        conditions.push({ 'treatment._id': { $in: idFilter.value.map(e => oid(e)) } })
      }

      if (conditions.length) {
        pipeline.push({ $match: { $and: conditions } })
      }

      pipeline.push({
        $project: {
          [pField]: 1,
          [iField]: 1,
          [levelField]: 1,
          eipdFinal: 1,
          'treatment.treatmentId': 1,
          'treatment.nombre': 1,
          'treatment._id': 1,
        }
      })

      const result = await getItemsAggregate({ collection, pipeline })

      const matrix = {}
      for (const item of (result || [])) {
        const p = item[pField]
        const i = item[iField]
        if (!p || !i) continue
        const key = `${p}-${i}`
        if (!matrix[key]) matrix[key] = []
        matrix[key].push({
          _id: item._id,
          treatmentId: item.treatment?.treatmentId || '',
          nombre: item.treatment?.nombre || '',
          level: item[levelField] || '',
          eipdFinal: item.eipdFinal,
          treatment_id: item.treatment?._id || '',
          p,
          i,
        })
      }

      matrixData.value = matrix
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar datos del mapa de riesgos' })
    } finally {
      isLoading.value = false
    }
  }

  const selectCell = (p, i) => {
    const key = `${p}-${i}`
    const items = matrixData.value[key] || []
    if (!items.length) return
    selectedCell.value = { p, i, risk: p * i }
    treatmentsInCell.value = items
  }

  const clearSelection = () => {
    selectedCell.value = null
    treatmentsInCell.value = []
  }

  watch([companyFilter, idFilter], () => {
    fetchRiskData()
  })

  watch(riskType, () => {
    clearSelection()
    fetchRiskData()
  })

  return {
    riskType,
    isLoading,
    matrixData,
    treatmentsInCell,
    selectedCell,
    summaryTable,
    totalTreatments,
    LEVELS,
    getCellColor,
    getRiskLevel,
    fetchRiskData,
    selectCell,
    clearSelection,
  }
}
